import type { ReactNode } from 'react'

import { useId } from 'react'
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'
import ChartTooltip from '@/components/charts/chart-tooltip'

interface TrendChartProps {
    data: Array<Record<string, string | number>>
    xKey: string
    yKey: string
    label: string
    ariaLabel: string
    color?: string
    height?: number
    valueFormatter?: (value: number) => string
    tickFormatter?: (value: number) => string
    tooltipLabelKey?: string
}

const AXIS_TICK = { fill: 'var(--muted-foreground)', fontSize: 12 }

// A single series over time: 2px line with a light wash underneath.
const TrendChart = ({
    data,
    xKey,
    yKey,
    label,
    ariaLabel,
    color = 'var(--chart-1)',
    height = 260,
    valueFormatter,
    tickFormatter,
    tooltipLabelKey
}: TrendChartProps): ReactNode => {
    const gradientId = `trend-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

    return (
        <figure className='grid gap-3'>
            <div style={{ height }} aria-hidden='true'>
                <ResponsiveContainer width='100%' height='100%'>
                    <AreaChart accessibilityLayer={false} data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                        <defs>
                            <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor={color} stopOpacity={0.16} />
                                <stop offset='100%' stopColor={color} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke='var(--chart-grid)' />
                        <XAxis
                            dataKey={xKey}
                            tickLine={false}
                            axisLine={{ stroke: 'var(--chart-axis)' }}
                            tick={AXIS_TICK}
                            interval='preserveStartEnd'
                            minTickGap={16}
                            dy={6}
                        />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tick={AXIS_TICK}
                            width={64}
                            tickFormatter={tickFormatter}
                        />
                        <Tooltip
                            cursor={{ stroke: 'var(--chart-axis)', strokeWidth: 1 }}
                            content={<ChartTooltip valueFormatter={valueFormatter} labelKey={tooltipLabelKey} />}
                        />
                        <Area
                            type='monotone'
                            dataKey={yKey}
                            name={label}
                            stroke={color}
                            strokeWidth={2}
                            fill={`url(#${gradientId})`}
                            dot={false}
                            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)', fill: color }}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            <table className='sr-only'>
                <caption>{ariaLabel}</caption>
                <thead>
                    <tr>
                        <th scope='col'>Period</th>
                        <th scope='col'>{label}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, index) => (
                        <tr key={index}>
                            <th scope='row'>{String(row[tooltipLabelKey ?? xKey] ?? row[xKey])}</th>
                            <td>{valueFormatter ? valueFormatter(Number(row[yKey] ?? 0)) : String(row[yKey] ?? 0)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}

export default TrendChart
